import { PageMode, ScaleMode, ViewMode } from './Enums';
import { IReportController } from './Interfaces';
import { DocumentInfo, PageAction, PageActionEventArgs, PageInfo, PageTargetElement } from './Models';
import { TooltipEventArgs } from './Models/TooltipEventArgs';
import { ResizeService } from './ResizeService';
import { ScrollManager } from './ScrollManager';
import { LocalizationMessageType } from './Types';
import { UIFreezeCoordinator } from './UIFreezeCoordinator';
import {
    addClass,
    createElement,
    getOffsetParent,
    hasClass,
    removeClass,
    toPixel,
    scaleElement
} from './Utils/DomUtils';

const stringFormatRegExp = /{(\w+?)}/g;
const pageAreaImageStyle = '.trv-page-container {background: #ffffff url("{0}") no-repeat center 50px}';
const pageAreaImageID = 'trv-initial-image-styles';

function stringFormat(template: string, data: any) {
    let isArray = Array.isArray(data);
    if (!template) {
        return '';
    }
    return template.replace(stringFormatRegExp, function($0, $1) {
        return data[isArray ? parseInt($1) : $1];
    });
}

export class ContentArea {
    private controller: IReportController;
    private actions: PageAction[] = [];
    private placeholder: HTMLElement;
    private pageContainer: HTMLElement;
    private pageWrapper: HTMLElement;
    private parametersContainer: HTMLElement;
    private notification: HTMLElement;
    private pendingElement: PageTargetElement | null = null;
    private documentReady: boolean = true;
    private reportPageIsLoaded: boolean = false;
    private navigateToPageOnDocReady: number = 0;
    private navigateToElementOnDocReady: PageTargetElement | null = null;
    private onClickHandler: any = null;
    private onMouseEnterHandler: any = null;
    private onMouseLeaveHandler: any = null;
    private isNewReportSource: boolean = false;
    private showNotificationTimeoutId: any;
    private resizeService: ResizeService;
    private scrollManager: ScrollManager;
    private messages: LocalizationMessageType;
    private uiFreezeCoordinator: UIFreezeCoordinator | null = null;
    private enableAccessibility: boolean;
    private initialPageAreaImageUrl: string = '';
    private showPageAreaImage: boolean = false;

    constructor(element: HTMLElement, controller: IReportController, messages: LocalizationMessageType, options: any = {}) {
        this.placeholder = element.querySelector('.trv-pages-pane, .trv-pages-area') as HTMLElement; // handle both old and new rendering
        this.pageContainer = element.querySelector('.trv-page-container') as HTMLElement;
        this.pageWrapper = element.querySelector('.trv-page-wrapper') as HTMLElement;
        this.parametersContainer = element.querySelector('.trv-parameters-area') as HTMLElement;
        this.notification = element.querySelector('.trv-notification, .trv-error-pane') as HTMLElement; // handle both old and new rendering

        this.scrollManager = new ScrollManager(this.placeholder, this, controller);

        this.resizeService = new ResizeService(this.pageContainer);

        this.resizeService.on('resize', this.onResize.bind(this));

        this.controller = controller;

        this.controller
            .on('pageReady', this.onPageReady.bind(this))
            .on('navigateToPage', this.navigateToPage.bind(this))
            .on('serverActionStarted', this.onServerActionStarted.bind(this))
            .on('reportSourceChanged', this.onReportSourceChanged.bind(this))
            .on('scaleChanged', this.updatePageDimensions.bind(this))
            .on('scaleModeChanged', this.updatePageDimensions.bind(this))
            .on('printStarted', this.onPrintStarted.bind(this))
            .on('printDocumentReady', this.onPrintDocumentReady.bind(this))
            .on('exportStarted', this.onExportStarted.bind(this))
            .on('exportDocumentReady', this.onExportDocumentReady.bind(this))
            .onAsync('beforeLoadReport', this.onBeforeLoadReport.bind(this))
            .on('beginLoadReport', this.onBeginLoadReport.bind(this))
            .on('reportLoadProgress', this.onReportLoadProgress.bind(this))
            .onAsync('reportLoadComplete', this.onReportLoadComplete.bind(this))
            .onAsync("reportAutoRunOff", this.onReportAutoRunOff.bind(this))
            .on('renderingStopped', this.onRenderingStopped.bind(this))
            .on('missingOrInvalidParameters', this.onMissingOrInvalidParameters.bind(this))
            .on('noReport', this.onNoReport.bind(this))
            .on('error', this.onError.bind(this));

        this.messages = messages;
        this.enableAccessibility = options.enableAccessibility || false;
        this.initialPageAreaImageUrl = options.initialPageAreaImageUrl || '';
    }

    destroy() {
        if (this.resizeService) {
            this.resizeService.destroy();
        }
    }

    private onResize() {
        if (this.shouldAutosizePage()) {
            this.updatePageDimensions();
        }
    }

    private onPageReady(pageInfo: PageInfo) {
        if (this.controller.getPageMode() === PageMode.SinglePage) {
            if (this.scrollManager.getEnabled()) {
                this.scrollManager.disable();
            }
            this.setPageContent(pageInfo);
        } else {
            this.scrollManager.renderPage(pageInfo);
        }

        if (!this.reportPageIsLoaded) {
            this.reportPageIsLoaded = true;
        }

        if (this.showPageAreaImage) {
            this.clearPageAreaImage();
        }

        if (this.controller.getViewMode() === ViewMode.Interactive && this.uiFreezeCoordinator !== null) {
            this.uiFreezeCoordinator.init(this.placeholder);
        }

        this.disablePagesArea(false);
    }

    private onServerActionStarted() {
        this.disablePagesArea(true);
        this.disableParametersArea(true);
        this.showNotification(this.messages['ReportViewer_LoadingReport']);
    }

    private onReportSourceChanged() {
        this.isNewReportSource = true;
        this.navigateToPageOnDocReady = 0;
        this.navigateToElementOnDocReady = null;
        this.documentReady = false;
    }

    private onNoReport() {
        this.disablePagesArea(false);
        this.disableParametersArea(false);
        this.clearPage();
        this.showNotification(this.messages['ReportViewer_NoReport']);
    }

    private onBeforeLoadReport() {
        this.documentReady = false;

        if (!this.navigateToPageOnDocReady) {
            this.navigateToPageOnDocReady = 1;
        }

        this.clearPendingTimeoutIds();
        this.clear();
        this.disablePagesArea(true);
        this.disableParametersArea(true);
        this.showNotification(this.messages['ReportViewer_LoadingReport']);
    }

    private onBeginLoadReport() {
        this.documentReady = true;

        this.invalidateCurrentlyLoadedPage();
    }

    private onReportLoadProgress(documentInfo: DocumentInfo) {
        // Temporary fix for issue #4504. 
        // To minimize any performance overhead the call will happen only between the 2nd and 10th pages 
        // with the hopes that onReportLoadProgress will be executed at least once even on very light reports
        if (documentInfo.pageCount > 1 && documentInfo.pageCount < 10 && this.controller.getPageMode() === PageMode.ContinuousScroll && !this.pageWrapper.classList?.contains('temp-height')) {
            this.pageWrapper.classList?.add('temp-height');
        }

        this.navigateWhenPageAvailable(this.navigateToPageOnDocReady, documentInfo.pageCount);
        this.showNotification(
            stringFormat(this.messages['ReportViewer_LoadingReportPagesInProgress'], [documentInfo.pageCount])
        );
    }

    private onReportLoadComplete(documentInfo: DocumentInfo) {
        if (documentInfo.pageCount === 0) {
            this.clearPage();
            this.showNotification(this.messages['ReportViewer_NoPageToDisplay']);

            this.pageWrapper.classList?.remove('temp-height');
        } else {
            if (documentInfo.pageCount === 1) {
                this.pageWrapper.classList?.remove('temp-height');
            }
            this.navigateOnLoadComplete(this.navigateToPageOnDocReady, documentInfo.pageCount);

            this.showNotification(
                stringFormat(this.messages['ReportViewer_LoadedReportPagesComplete'], [documentInfo.pageCount])
            );
            this.showNotificationTimeoutId = window.setTimeout(this.hideNotification.bind(this), 2000);

            this.disableParametersArea(false);
            this.enableInteractivity();
        }

        if (documentInfo.containsFrozenContent && this.uiFreezeCoordinator === null) {
            this.uiFreezeCoordinator = new UIFreezeCoordinator();

            if (this.controller.getViewMode() === ViewMode.Interactive) {
                this.uiFreezeCoordinator.init(this.placeholder);
            }
        }
    }

    private onReportAutoRunOff() {
        this.disableParametersArea(false);
        this.showNotification(this.messages['ReportViewer_AutoRunDisabled'] || "Please validate the report parameter values and press Preview to generate the report.");
    }

    private onRenderingStopped() {
        this.clear(true);
        this.disableParametersArea(false);
        this.showError(this.messages['ReportViewer_RenderingCancelled']);
    }

    private onMissingOrInvalidParameters() {
        if (this.initialPageAreaImageUrl && !this.reportPageIsLoaded) {
            this.clearPage();
            this.setPageAreaImage();
        }
    }

    private onError(message: string, shouldClearReport: boolean = true) {
        if (shouldClearReport) {
            this.disablePagesArea(false);
            this.disableParametersArea(false);
            this.clearPage();
        }

        this.showError(message);
    }

    private clearPendingTimeoutIds() {
        if (this.showNotificationTimeoutId) {
            window.clearTimeout(this.showNotificationTimeoutId);
        }
    }

    private invalidateCurrentlyLoadedPage() {
        let page = this.findPage(this.navigateToPageOnDocReady);
        if (page) {
            this.setPageNo(page, -1);
        }
    }

    private navigateWhenPageAvailable(pageNumber: number, pageCount: number) {
        if (pageNumber && pageNumber <= pageCount) {
            this.navigateToPage(pageNumber, this.navigateToElementOnDocReady);
        }
    }

    private navigateOnLoadComplete(pageNumber: number, pageCount: number) {
        if (pageNumber) {
            // eslint-disable-next-line no-param-reassign
            pageNumber = Math.min(pageNumber, pageCount);

            this.navigateToPage(pageNumber, this.navigateToElementOnDocReady);
        }
    }

    private clearPage() {
        this.clear(this.isNewReportSource);
        this.isNewReportSource = false;
    }

    private shouldAutosizePage() {
        let scaleMode = this.controller.getScaleMode();

        return scaleMode === ScaleMode.FitPage || scaleMode === ScaleMode.FitPageWidth;
    }

    private onPrintStarted() {
        this.showNotification(this.messages['ReportViewer_PreparingPrint']);
    }

    private onPrintDocumentReady() {
        this.hideNotification();
    }

    private onExportStarted() {
        this.showNotification(this.messages['ReportViewer_PreparingDownload']);
    }

    private onExportDocumentReady() {
        this.hideNotification();
    }

    private updatePageDimensions() {
        let allPages = this.pageContainer.querySelectorAll('.trv-report-page');
        for (let page of Array.from(allPages)) {
            let pageElement = page as HTMLElement;

            let pageNumber = parseInt(pageElement.dataset['page'] || '');
            this.setPageDimensions(pageElement, pageNumber);
        }

        this.scrollManager.updatePageDimensionsReady();
    }

    private clear(clearPageWrapper: boolean = false) {
        this.disableInteractivity();

        this.pendingElement = null;

        if (clearPageWrapper) {
            this.pageWrapper.innerHTML = '';
        }

        this.hideNotification();
    }

    private findPage(pageNumber: number): HTMLElement | null {
        let allPages = this.pageContainer.querySelectorAll('.trv-report-page');

        if (this.controller.getPageMode() === PageMode.SinglePage) {
            for (let page of Array.from(allPages)) {
                let pageElement = page as HTMLElement;

                if (this.pageNo(pageElement) === pageNumber) {
                    return pageElement;
                }
            }
        } else {
            for (let page of Array.from(allPages)) {
                let pageElement = page as HTMLElement;

                if (parseInt(pageElement?.dataset['page'] || '-1') === pageNumber) {
                    return pageElement;
                }
            }
        }

        return null;
    }

    private navigateToPage(pageNumber: number, targetElement: PageTargetElement | null) {
        if (this.documentReady) {
            this.navigateToPageCore(pageNumber, targetElement);
        } else {
            this.navigateToPageOnDocumentReady(pageNumber, targetElement);
        }
    }

    private navigateToPageOnDocumentReady(pageNumber: number, targetElement: PageTargetElement | null) {
        this.navigateToPageOnDocReady = pageNumber;
        this.navigateToElementOnDocReady = targetElement;
    }

    private navigateToPageCore(pageNumber: number, targetElement: PageTargetElement | null) {
        let page = this.findPage(pageNumber);
        if (page) {
            if (targetElement) {
                this.navigateToElement(targetElement, pageNumber);
            }

            if (this.scrollManager.getEnabled() && !targetElement) {
                this.scrollManager.navigateToElement(page.offsetTop, pageNumber);
            }
        } else {
            this.pendingElement = targetElement;
            this.beginLoadPage(pageNumber);
        }
    }

    private navigateToElement(targetElement: PageTargetElement | null, pageNumber: number = 0) {
        if (targetElement) {
            let el = this.pageContainer.querySelector(
                `[data-${targetElement.type}-id='${targetElement.id}']`
            ) as HTMLElement | null;
            if (el) {
                if (this.enableAccessibility) {
                    //finds next focusable element, because the bookmark might lead to a panel, which is non-focusable.
                    let nextFocusable = this.findNextFocusableElement(el);
                    if (nextFocusable) {
                        nextFocusable.focus();
                    }
                }

                let offsetTop = 0;
                let offsetLeft = 0;
                while (el && el !== this.pageContainer) {
                    if (hasClass(el, 'trv-page-wrapper')) {
                        let scale = el.dataset['pageScale'];
                        if (typeof scale === 'string') {
                            let scaleNum = parseFloat(scale);
                            offsetTop *= scaleNum;
                            offsetLeft *= scaleNum;
                        }
                    }

                    offsetTop += el.offsetTop;
                    offsetLeft += el.offsetLeft;

                    el = getOffsetParent(el);
                }

                if (this.scrollManager.getEnabled() && pageNumber) {
                    this.scrollManager.navigateToElement(offsetTop, pageNumber);
                } else {
                    this.pageContainer.scrollTop = offsetTop;
                    this.pageContainer.scrollLeft = offsetLeft;
                }
            } else {
                if (this.scrollManager.getEnabled() && pageNumber) {
                    this.scrollManager.navigateToElement(
                        (this.placeholder?.querySelector('[data-page="' + pageNumber + '"]') as HTMLElement).offsetTop,
                        pageNumber
                    );
                }
            }
        }
    }

    private findNextFocusableElement(element: HTMLElement | null): HTMLElement | null {
        if (!element) {
            return null;
        }

        let num = element.tabIndex;
        if (!isNaN(num) && num > -1) {
            return element;
        }

        return this.findNextFocusableElement(element.nextElementSibling as HTMLElement);
    }

    private disablePagesArea(disable: boolean) {
        if (disable) {
            addClass(this.placeholder, 'trv-loading');
        } else {
            removeClass(this.placeholder, 'trv-loading');
        }
    }

    private disableParametersArea(disable: boolean) {        
        if (disable) {
            this.parametersContainer?.setAttribute('disabled', '');
        } else {
            this.parametersContainer?.removeAttribute('disabled');
        }
    }

    private showError(message: string = '') {
        this.showNotification(message, 'error');
    }

    private showNotification(message: string = '', type: string = 'info') {
        this.notification.dataset['type'] = type || 'info';
        let notificationContent = <HTMLElement>(
            this.notification.querySelector('.k-notification-content, .trv-error-message') // handle both old and new rendering
        );

        let splitMessage = message?.split(/\r?\n/);
        notificationContent.innerHTML = (splitMessage && splitMessage.length) ? `${splitMessage.join('<br>')}` : "Notification message not found.";

        addClass(this.notification, `k-notification-${type}`);
        removeClass(this.notification, 'k-hidden');
    }
    private hideNotification() {
        let type = String(this.notification.dataset['type']);
        delete this.notification.dataset['type'];

        removeClass(this.notification, `k-notification-${type}`);
        addClass(this.notification, 'k-hidden');
    }

    private pageNo(page: HTMLElement): number {
        let dataKey = 'pageNumber';

        return parseInt(page.parentElement?.dataset[dataKey] || '0');
    }

    private setPageNo(page: HTMLElement, no: number) {
        let dataKey = 'pageNumber';

        if (page.parentElement) {
            page.parentElement.dataset[dataKey] = no.toString();
        }
    }

    private beginLoadPage(pageNumber: number) {
        this.disablePagesArea(true);
        window.setTimeout(this.controller.getReportPage.bind(this.controller, pageNumber), 1);
        this.navigateToPageOnDocReady = 0;
    }

    private setPageDimensions(element: HTMLElement, pageNumber: number = 0) {
        let scaleMode = this.controller.getScaleMode();
        let scale = this.controller.getScale();
        /* Since CONTINUOUS_SCROLL has been added target is sometimes .trv-page-wrapper, sometimes .trv-report-page ¯\_(ツ)_/¯ */
        let page = pageNumber ? element : (element.querySelector('div.trv-report-page') as HTMLElement);

        if (!page) {
            return;
        }

        let pageContent = page.querySelector('div.sheet') as HTMLElement;
        let pageSkeletonContent = page.querySelector('div.trv-skeleton-wrapper') as HTMLElement;

        pageContent = pageContent || pageSkeletonContent;

        if (!pageContent) return;

        let pageWidth: number = 0;
        let pageHeight: number = 0;
        let computedStyle = getComputedStyle(page);
        let elementComputedStyle = getComputedStyle(element);

        let box = {
            padLeft:
                toPixel(elementComputedStyle.marginLeft) +
                toPixel(computedStyle.borderLeftWidth) +
                toPixel(computedStyle.paddingLeft),
            padRight:
                toPixel(elementComputedStyle.marginRight) +
                toPixel(computedStyle.borderRightWidth) +
                toPixel(computedStyle.paddingRight),
            padTop:
                toPixel(elementComputedStyle.marginTop) +
                toPixel(computedStyle.borderTopWidth) +
                toPixel(computedStyle.paddingTop),
            padBottom:
                toPixel(elementComputedStyle.marginBottom) +
                toPixel(computedStyle.borderBottomWidth) +
                toPixel(computedStyle.paddingBottom)
        };

        pageWidth = pageContent.offsetWidth;
        pageHeight = pageContent.offsetHeight;

        // All dimensions from the styles:
        let scrollBarV = pageHeight > pageWidth && scaleMode === ScaleMode.FitPageWidth ? 20 : 0;
        let scaleW = (this.pageContainer.clientWidth - scrollBarV - box.padLeft - box.padRight) / pageWidth;
        let scaleH = (this.pageContainer.clientHeight - 1 - box.padTop - box.padBottom) / pageHeight;

        if (scaleMode === ScaleMode.FitPageWidth) {
            scale = scaleW;
        } else if (!scale || scaleMode === ScaleMode.FitPage) {
            scale = Math.min(scaleW, scaleH);
        }

        if (this.uiFreezeCoordinator !== null) {
            this.uiFreezeCoordinator.setScaleFactor(scale);
        }

        element.dataset['pageScale'] = scale.toString();
        page.dataset['pageScale'] = scale.toString();

        if (!pageSkeletonContent) {
            scaleElement(pageContent, scale, scale);
        }

        page.style.height = scale * pageHeight + 'px';
        page.style.width = scale * pageWidth + 'px';
    }

    private enableInteractivity() {
        this.onClickHandler = this.onClick.bind(this);
        this.onMouseEnterHandler = this.onMouseEnter.bind(this);
        this.onMouseLeaveHandler = this.onMouseLeave.bind(this);

        this.pageContainer.addEventListener('click', this.onClickHandler);
        this.pageContainer.addEventListener('mouseenter', this.onMouseEnterHandler, true);
        this.pageContainer.addEventListener('mouseleave', this.onMouseLeaveHandler, true);
    }

    private disableInteractivity() {
        this.pageContainer.removeEventListener('click', this.onClickHandler);
        this.pageContainer.removeEventListener('mouseenter', this.onMouseEnterHandler);
        this.pageContainer.removeEventListener('mouseleave', this.onMouseLeaveHandler);
    }

    private onClick(event: MouseEvent) {
        let actionElement = (event.target as HTMLElement).closest('[data-reporting-action]');

        if (actionElement) {
            this.onInteractiveItemClick(actionElement as HTMLElement, event);
        }
    }

    private onMouseEnter(event: MouseEvent) {
        let element = (event.target as HTMLElement);
        let targetAttributes = element.dataset;

        if (targetAttributes['reportingAction']) {
            this.onInteractiveItemEnter(element);
        }

        if (targetAttributes['tooltipTitle'] || targetAttributes['tooltipText']) {
            this.onToolTipItemEnter(element, event);
        }
    }

    private onMouseLeave(event: MouseEvent) {
        let element = (event.target as HTMLElement);
        let targetAttributes = element.dataset;

        if (targetAttributes['reportingAction']) {
            this.onInteractiveItemLeave(element);
        }

        if (targetAttributes['tooltipTitle'] || targetAttributes['tooltipText']) {
            this.onToolTipItemLeave(element);
        }
    }

    private onInteractiveItemClick(element: HTMLElement, event: MouseEvent) {
        let actionId = element.dataset['reportingAction']?.toString() || '';
        let action = this.getAction(actionId);

        if (action) {
            this.navigateToPageOnDocReady = this.getNavigateToPageOnDocReady(event, action.Type);

            this.controller.executeReportAction(new PageActionEventArgs(element, action));
        }

        event.stopPropagation();
    }

    private onInteractiveItemEnter(element: HTMLElement) {
        let actionId = element.dataset['reportingAction']?.toString() || '';
        let action = this.getAction(actionId);

        if (action) {
            this.controller.reportActionEnter(new PageActionEventArgs(element, action));
        }
    }

    private onInteractiveItemLeave(element: HTMLElement) {
        let actionId = element.dataset['reportingAction']?.toString() || '';
        let action = this.getAction(actionId);

        if (action) {
            this.controller.reportActionLeave(new PageActionEventArgs(element, action));
        }
    }

    private onToolTipItemEnter(element: HTMLElement, event: MouseEvent) {
        let title = element.dataset['tooltipTitle'];
        let text = element.dataset['tooltipText'];

        if (!title && !text) {
            return;
        }

        this.controller.reportTooltipOpening(new TooltipEventArgs(element, text || '', title || '', event));
    }

    private onToolTipItemLeave(element: HTMLElement) {
        this.controller.reportTooltipClosing(new TooltipEventArgs(element, '', '', null));
    }

    private getNavigateToPageOnDocReady(event: any, actionType: string): number {
        if (this.scrollManager.getEnabled() && (actionType === 'sorting' || actionType === 'toggleVisibility')) {
            return event.target.closest('.trv-report-page')?.dataset['page'] || this.controller.getCurrentPageNumber();
        }

        return this.controller.getCurrentPageNumber();
    }

    private getAction(actionId: string): PageAction | null {
        if (this.actions) {
            let action = this.actions.find((a) => a.Id === actionId);

            if (action) {
                return action;
            }
        }

        return null;
    }

    updatePageStyle(pageInfo: PageInfo) {
        //This is correct only for one page displaying mode.
        let styleId = 'trv-' + this.controller.getClientId() + '-styles';
        document.getElementById(styleId)?.remove();

        let pageStyles = createElement('style', styleId);
        pageStyles.innerHTML = pageInfo.pageStyles;
        document.head.appendChild(pageStyles);
    }

    private setPageContent(pageInfo: PageInfo) {
        this.actions = pageInfo.pageActions;

        this.updatePageStyle(pageInfo);

        let page = this.renderPageElement(pageInfo);

        this.pageWrapper.dataset['pageNumber'] = pageInfo.pageNumber.toString();
        this.pageWrapper.innerHTML = '';
        this.pageWrapper.append(page);

        this.controller.setCurrentPageNumber(pageInfo.pageNumber);

        this.applyPlaceholderViewModeClass();

        this.setPageDimensions(this.pageWrapper);

        // each new page should start from top/left. work item: 256178
        this.pageContainer.scrollTop = 0;
        this.pageContainer.scrollLeft = 0;

        this.navigateToElement(this.pendingElement);
    }

    setScrollablePage(page: HTMLElement, pageInfo: PageInfo) {
        if (!this.actions || !this.actions.length) {
            this.actions = pageInfo.pageActions;
        } else {
            this.actions = this.actions.concat(pageInfo.pageActions);
        }

        this.applyPlaceholderViewModeClass();

        this.setPageDimensions(page, pageInfo.pageNumber);
    }

    renderPageElement(pageInfo: PageInfo): HTMLElement {
        let temp = createElement('div');
        temp.innerHTML = pageInfo.pageContent;

        let pageContent = temp.querySelector('div.sheet') as HTMLElement;
        pageContent.style.margin = '0';

        let page = createElement('div', '', 'trv-report-page');
        page.dataset['page'] = pageInfo.pageNumber.toString();

        page.append(pageContent);
        page.append(createElement('div', '', 'k-overlay trv-overlay trv-page-overlay')); // handle both old and new rendering

        return page;
    }

    private applyPlaceholderViewModeClass() {
        if (this.controller.getViewMode() === ViewMode.Interactive) {
            removeClass(this.placeholder, 'printpreview');
            addClass(this.placeholder, 'interactive');
        } else {
            removeClass(this.placeholder, 'interactive');
            addClass(this.placeholder, 'printpreview');
        }
    }

    private setPageAreaImage() {
        this.clearPageAreaImage();

        let pageStyles = createElement('style', pageAreaImageID);
        pageStyles.innerHTML = stringFormat(pageAreaImageStyle, [this.initialPageAreaImageUrl]);
        document.head.appendChild(pageStyles);

        this.showPageAreaImage = true;
    }

    private clearPageAreaImage() {
        document.getElementById(pageAreaImageID)?.remove();
    }
}
